import React, { useMemo, useState } from 'react';
import { klona } from 'klona';
import { Container, Section, Heading } from '@printdeal/ui-library';
import { useReadyForCypress } from '../../hooks/useReadyForCypress';
import ProductGroupSection from './ProductGroupSection';
import { isAlternativeImage, isGroup } from './CategoryPage';
import type { Product } from '../../types/category/category';
import type { GroupOrItem } from './CategoryPage';
import { ExploreDesignTemplateButton } from '../design-template/exploreDesignTemplateButton';
import { QuickViewModal } from './quick-view/QuickViewModal';

interface Props {
  categoryName: string;
  title?: string;
  products: GroupOrItem[];
  isQuickViewExp: boolean;
}

export interface ProductSection {
  __typename: 'ContentfulProductContent' | 'ContentfulProductListingGroup';
  id: string;
  title?: string;
  description?: string;
  backgroundColor?: string;
  products: Product[];
  readMoreLinkText?: string;
  readMoreLink?: {
    path: string;
  }
}

const isProduct = (item: GroupOrItem): item is Product => (
  // eslint-disable-next-line no-underscore-dangle
  item.__typename === 'ContentfulProductContent'
);

const mapToGroups = (items: GroupOrItem[]) =>
  items.reduce((groups, productOrGroup, index, source) => {
    if (isProduct(productOrGroup) || isAlternativeImage(productOrGroup)) {
      // if product is first item or previous item is a group, create a new group
      if (index === 0 || isGroup(source[index - 1])) {
        // if product has alternative image, convert it to product
        const productList = isAlternativeImage(productOrGroup) ? {
          ...productOrGroup.productDetailPage,
          image: productOrGroup.alternativeImage,
        } : productOrGroup;

        const newGroup: ProductSection = {
          __typename: 'ContentfulProductContent',
          id: productOrGroup.id,
          products: [productList],
        };

        return [...groups, newGroup];
      }

      // if product is not first item and previous item is not a group, add product to previous group
      if (isProduct(productOrGroup) || isAlternativeImage(productOrGroup)) {
        const cloneGroups = klona(groups);

        if (isAlternativeImage(productOrGroup)) {
          cloneGroups[cloneGroups.length - 1] = {
            ...cloneGroups[cloneGroups.length - 1],
            products: [...cloneGroups[cloneGroups.length - 1].products, {
              ...productOrGroup.productDetailPage,
              image: productOrGroup.alternativeImage,
            }],
          };
          return cloneGroups;
        }

        if (isProduct(productOrGroup)) {
          cloneGroups[cloneGroups.length - 1] = {
            ...cloneGroups[cloneGroups.length - 1],
            products: [...cloneGroups[cloneGroups.length - 1].products, productOrGroup],
          };
          return cloneGroups;
        }
        return cloneGroups;
      }
    }

    if (isGroup(productOrGroup) && productOrGroup.products.length > 0) {
      // if group contains products with alternative images, convert them to products
      const products = productOrGroup.products.reduce((group, product) => {
        if (isAlternativeImage(product)) {
          return [...group, {
            ...product.productDetailPage,
            image: product.alternativeImage,
          }];
        }
        return [...group, product];
      }, [] as Product[]);

      const newGroup: ProductSection = {
        ...productOrGroup,
        id: productOrGroup.id,
        products,
      };
      return [...groups, newGroup];
    }

    return groups;
  }, [] as ProductSection[]);

export type QuickViewModalState = {
  reviews?: {
    score: number,
    totalReviews: number,
  },
  path: string,
  contentfulId: string,
};

const ProductsSection = ({
  categoryName, title = '', products = [], isQuickViewExp,
}: Props) => {
  const [quickViewModal, setQuickViewModal] = useState<QuickViewModalState | null>(null);
  const productGroups = useMemo(() => mapToGroups(products), [products]);
  const readyForCypressId = useReadyForCypress('product-category-page-products-loaded');

  return (
    <Section className="pd-grid pd-gap-4 pd-pt-0" data-cy={readyForCypressId}>
      {title && (
        <Container>
          <Heading as="div" size="2">{title} </Heading>
          <ExploreDesignTemplateButton label="category_link" isButton={false} />
        </Container>
      )}
      <div className="pd-flex pd-flex-col pd-gap-12">
        {productGroups.map((group, groupIndex) => (
          <ProductGroupSection
            key={group.id}
            categoryName={categoryName}
            group={group}
            groupIndex={groupIndex}
            setQuickViewModal={setQuickViewModal}
            isQuickViewExp={isQuickViewExp}
          />
        ))}
        {quickViewModal?.path && quickViewModal && (
          <QuickViewModal quickViewModal={quickViewModal} setQuickViewModal={setQuickViewModal} />
        )}
      </div>
    </Section>
  );
};

export default ProductsSection;
